import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Reorder, useDragControls, motion } from "framer-motion";
import { GripVertical, Trash2, Type, Image as ImageIcon, Table as TableIcon, Video, Images, ClipboardList, Plus, Loader2, Save, X, Upload, Eye, EyeOff, ChevronLeft, ChevronRight, Paperclip, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ArticleBody from "@/components/ArticleBody";
import RichTextEditor from "@/components/RichTextEditor";
import { FlexibleTableView, FlexibleTableEditor } from "@/components/FlexibleTable";
import { formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { uploadAnyFile } from "@/lib/uploads";
import AttachmentCard from "@/components/AttachmentCard";

const uid = () => Math.random().toString(36).slice(2, 9);

export function getVideoEmbed(url) {
  if (!url) return null;
  if (/\/api\/media\//.test(url)) return { type: "file", src: url };
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host.includes("youtube.com")) { const id = u.searchParams.get("v"); if (id) return { type: "iframe", src: `https://www.youtube.com/embed/${id}` }; }
    if (host === "youtu.be") { const id = u.pathname.slice(1); if (id) return { type: "iframe", src: `https://www.youtube.com/embed/${id}` }; }
    if (host.includes("vimeo.com")) { const id = u.pathname.split("/").filter(Boolean)[0]; if (id) return { type: "iframe", src: `https://player.vimeo.com/video/${id}` }; }
    if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) return { type: "file", src: url };
    return null;
  } catch { return null; }
}

const CT_EXT = [["pdf", "pdf"], ["wordprocessingml", "docx"], ["msword", "doc"], ["spreadsheetml", "xlsx"], ["ms-excel", "xls"], ["presentationml", "pptx"], ["ms-powerpoint", "ppt"], ["zip", "zip"], ["video/", "mp4"], ["audio/", "mp3"]];

// Normalizza un blocco immagine in una lista ordinata di immagini (retrocompatibile con il vecchio campo singolo `url`)
export function imageItemsOf(block) {
  if (Array.isArray(block.images) && block.images.length) return block.images;
  if (block.url) return [{ url: block.url, caption: block.caption || "", kind: block.kind, filename: block.filename, size: block.size, link: block.link }];
  return [];
}

// Immagine proporzionata e cliccabile (lightbox o link); se il file non è un'immagine (es. PDF caricato come immagine) mostra la scheda allegato
function ImageBlockView({ block }) {
  const [failed, setFailed] = useState(false);
  const [ext, setExt] = useState("");
  const [open, setOpen] = useState(false);
  const src = mediaUrl(block.url);
  const onError = async () => {
    setFailed(true);
    const ctrl = new AbortController();
    try {
      const r = await fetch(src, { method: "GET", signal: ctrl.signal });
      const ct = (r.headers.get("content-type") || "").toLowerCase();
      ctrl.abort();
      setExt((CT_EXT.find(([k]) => ct.includes(k)) || ["", "bin"])[1]);
    } catch { setExt("bin"); }
  };
  if (failed) {
    const base = (block.filename || block.caption || "Documento").replace(/\.[a-z0-9]+$/i, "");
    return <AttachmentCard url={block.url} name={ext ? `${base}.${ext}` : base} size={block.size} className="my-0" />;
  }
  const img = <img src={src} alt={block.caption || ""} onError={onError} className="w-full h-auto object-contain block bg-[#0B1220]" />;
  const isExternal = block.link && /^https?:\/\//i.test(block.link);
  return (
    <figure className="rounded-xl overflow-hidden border border-white/10" data-testid="image-block">
      {block.link ? (
        isExternal
          ? <a href={block.link} target="_blank" rel="noopener noreferrer" data-testid="image-block-link" className="block">{img}</a>
          : <Link to={block.link} data-testid="image-block-link" className="block">{img}</Link>
      ) : (
        <button type="button" data-testid="image-block-open" onClick={() => setOpen(true)} className="block w-full cursor-zoom-in relative group text-left">
          {img}
          <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity"><Maximize2 className="w-4 h-4" /></span>
        </button>
      )}
      {block.caption ? <figcaption className="px-4 py-2 text-sm text-muted-foreground bg-[#0B1220]">{block.caption}</figcaption> : null}
      {open && (
        <div data-testid="image-lightbox" className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-3 overflow-auto" onClick={() => setOpen(false)}>
          <button type="button" data-testid="image-lightbox-close" onClick={() => setOpen(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"><X className="w-5 h-5" /></button>
          <img src={src} alt={block.caption || ""} className="max-w-full max-h-[92vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </figure>
  );
}

function ImageItemView({ item }) {
  if (item.kind && item.kind !== "image") return <AttachmentCard url={item.url} name={item.filename || item.caption || "Allegato"} size={item.size} className="my-0" />;
  return <ImageBlockView block={item} />;
}

// Riga immagine trascinabile per il blocco "Immagine" (riordino verticale)
function BlockImageItem({ item, idx, si, bi, onCaption, onLink, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls} data-testid={`block-${si}-${bi}-img-${idx}`} className="rounded-lg border border-white/10 p-2 bg-black/20">
      <div className="flex items-start gap-2">
        <button type="button" onPointerDown={(e) => controls.start(e)} data-testid={`block-${si}-${bi}-img-${idx}-drag`} title="Trascina per riordinare" className="shrink-0 p-1.5 text-muted-foreground hover:text-gold cursor-grab active:cursor-grabbing touch-none"><GripVertical className="w-4 h-4" /></button>
        <div className="shrink-0">
          {item.kind && item.kind !== "image"
            ? <div className="w-20 h-20 flex flex-col items-center justify-center gap-1 bg-black/40 text-gold rounded border border-white/10"><Paperclip className="w-5 h-5" /><span className="text-[10px] text-muted-foreground truncate max-w-[72px]">{item.filename || "Allegato"}</span></div>
            : <img src={mediaUrl(item.url)} alt="" className="w-20 h-20 object-cover rounded border border-white/10" />}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <Input data-testid={`block-${si}-${bi}-img-${idx}-caption`} value={item.caption || ""} onChange={(e) => onCaption(e.target.value)} placeholder="Didascalia (facoltativa)" className="h-9" />
          <Input data-testid={`block-${si}-${bi}-img-${idx}-link`} value={item.link || ""} onChange={(e) => onLink(e.target.value)} placeholder="Link al clic (facoltativo)" className="h-9" />
        </div>
        <button type="button" data-testid={`block-${si}-${bi}-img-${idx}-remove`} onClick={onRemove} title="Rimuovi immagine" className="shrink-0 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
      </div>
    </Reorder.Item>
  );
}

// Cella galleria trascinabile (riordino nella griglia)
function GalleryImageItem({ item, idx, si, bi, onCaption, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls} data-testid={`block-${si}-${bi}-img-${idx}`} className="rounded-lg border border-white/10 overflow-hidden bg-black/20">
      <div className="relative w-full" style={{ paddingTop: "70%" }}>
        {item.kind && item.kind !== "image"
          ? <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 text-gold px-2"><Paperclip className="w-6 h-6" /><span className="text-[11px] text-muted-foreground truncate max-w-full">{item.filename || "Allegato"}</span></div>
          : <img src={item.url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <button type="button" onPointerDown={(e) => controls.start(e)} data-testid={`block-${si}-${bi}-img-${idx}-drag`} title="Trascina per riordinare" className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/70 text-white hover:bg-gold hover:text-[#020617] flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"><GripVertical className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`block-${si}-${bi}-img-${idx}-remove`} onClick={onRemove} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 text-white hover:bg-destructive flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <Input data-testid={`block-${si}-${bi}-img-${idx}-caption`} value={item.caption} onChange={(e) => onCaption(e.target.value)} placeholder="Didascalia" className="h-8 text-xs border-0 rounded-none" />
    </Reorder.Item>
  );
}

export function BlockView({ block, onRegister }) {
  if (block.type === "text") return block.html ? <ArticleBody text={block.html} className="text-muted-foreground" /> : null;
  if (block.type === "video" && block.url && block.kind && block.kind !== block.type) {
    return <AttachmentCard url={block.url} name={block.filename || block.caption || "Allegato"} size={block.size} className="my-0" />;
  }
  if (block.type === "file") return block.url ? <AttachmentCard url={block.url} name={block.filename || block.caption || "Allegato"} size={block.size} className="my-0" /> : null;
  if (block.type === "image") {
    const items = imageItemsOf(block);
    if (!items.length) return null;
    return <div className="space-y-4" data-testid="image-block-list">{items.map((it, i) => <ImageItemView key={i} item={it} />)}</div>;
  }
  if (block.type === "table") return <FlexibleTableView columns={block.columns || []} rows={block.rows || []} caption={block.caption} />;
  if (block.type === "video") {
    const e = getVideoEmbed(block.url);
    if (!e) return null;
    return (
      <figure className="rounded-xl overflow-hidden border border-white/10">
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
          {e.type === "iframe" ? (
            <iframe title={block.caption || "Video"} src={e.src} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" />
          ) : (
            <video src={e.src} controls className="absolute inset-0 w-full h-full bg-black" />
          )}
        </div>
        {block.caption ? <figcaption className="px-4 py-2 text-sm text-muted-foreground bg-[#0B1220]">{block.caption}</figcaption> : null}
      </figure>
    );
  }
  if (block.type === "gallery") {
    return <GalleryView images={block.images || []} slideshow={block.slideshow} />;
  }
  if (block.type === "registration") {
    const heading = block.html || "Iscrizione gare";
    const desc = block.caption || "Compila il modulo di iscrizione dedicato alla disciplina.";
    return (
      <div data-testid="section-iscrizione" className="rounded-2xl border border-gold/20 bg-gradient-to-b from-gold/5 to-transparent p-8">
        <h3 className="font-display text-2xl">{heading}</h3>
        <p className="text-muted-foreground text-sm mt-1">{desc}</p>
        {onRegister ? (
          <Button data-testid="open-registration-btn" onClick={onRegister} className="mt-6 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold"><ClipboardList className="w-4 h-4 mr-1.5" /> Compila iscrizione</Button>
        ) : (
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/disciplina/artistica" className="rounded-full bg-gold text-[#020617] px-5 py-2.5 text-sm font-semibold hover:bg-[#FDE047] transition-colors">Iscrizione Artistica</Link>
            <Link to="/disciplina/ritmica" className="rounded-full border border-gold/40 text-gold px-5 py-2.5 text-sm font-semibold hover:bg-gold/10 transition-colors">Iscrizione Ritmica</Link>
          </div>
        )}
      </div>
    );
  }
  return null;
}

function GalleryView({ images, slideshow = false }) {
  const all = images || [];
  const imgs = all.filter((im) => !im.kind || im.kind === "image");
  const files = all.filter((im) => im.kind && im.kind !== "image");
  const [lightbox, setLightbox] = useState(null); // index
  const [slide, setSlide] = useState(0);
  const auto = slideshow && imgs.length > 1;

  useEffect(() => {
    if (!auto || lightbox !== null) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % imgs.length), 4000);
    return () => clearInterval(t);
  }, [auto, imgs.length, lightbox]);

  const filesEl = files.length > 0 && (
    <div className="space-y-2 mt-3" data-testid="gallery-files">
      {files.map((f, i) => <AttachmentCard key={`${f.url}-${i}`} url={f.url} name={f.filename || f.caption || "Allegato"} size={f.size} className="my-0" testid={`gallery-file-${i}`} />)}
    </div>
  );
  if (!imgs.length) return filesEl || null;
  const close = () => setLightbox(null);
  const go = (dir) => setLightbox((i) => (i + dir + imgs.length) % imgs.length);
  const slideTo = (dir) => setSlide((s) => (s + dir + imgs.length) % imgs.length);

  const lightboxEl = lightbox !== null && (
    <div data-testid="gallery-lightbox" className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
      <button data-testid="gallery-lightbox-close" onClick={close} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
      {imgs.length > 1 && (
        <>
          <button data-testid="gallery-lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>
          <button data-testid="gallery-lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>
        </>
      )}
      <figure className="max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <img src={imgs[lightbox].url} alt={imgs[lightbox].caption || ""} className="max-w-full max-h-[82vh] object-contain rounded-lg" />
        {imgs[lightbox].caption ? <figcaption className="text-center text-sm text-white/80 mt-3">{imgs[lightbox].caption}</figcaption> : null}
      </figure>
    </div>
  );

  if (slideshow) {
    const cur = imgs[slide] || imgs[0];
    return (
      <>
        <div data-testid="gallery-slideshow" className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <button type="button" data-testid={`gallery-img-${slide}`} onClick={() => setLightbox(slide)} className="relative w-full block cursor-zoom-in" style={{ paddingTop: "56%" }} aria-label="Apri immagine">
            {imgs.map((im, i) => (
              <img key={i} src={im.url} alt={im.caption || ""} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0"}`} />
            ))}
          </button>
          {cur.caption ? <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-5 py-3 text-sm text-white">{cur.caption}</div> : null}
          {imgs.length > 1 && (
            <>
              <button data-testid="gallery-slide-prev" onClick={() => slideTo(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-gold hover:text-[#020617] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ChevronLeft className="w-5 h-5" /></button>
              <button data-testid="gallery-slide-next" onClick={() => slideTo(1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-gold hover:text-[#020617] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-5 h-5" /></button>
              <div className="absolute bottom-3 inset-x-0 flex justify-center gap-2">
                {imgs.map((_, i) => (
                  <button key={i} data-testid={`gallery-dot-${i}`} onClick={() => setSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === slide ? "bg-gold w-5" : "bg-white/50 hover:bg-white"}`} aria-label={`Vai a immagine ${i + 1}`} />
                ))}
              </div>
            </>
          )}
        </div>
        {filesEl}
        {lightboxEl}
      </>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gallery-grid">
        {imgs.map((im, i) => (
          <figure key={i} className="rounded-xl overflow-hidden border border-white/10 group">
            <button type="button" data-testid={`gallery-img-${i}`} onClick={() => setLightbox(i)} className="relative w-full block cursor-zoom-in" style={{ paddingTop: "75%" }} aria-label="Apri immagine">
              <img src={im.url} alt={im.caption || ""} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </button>
            {im.caption ? <figcaption className="px-3 py-1.5 text-xs text-muted-foreground bg-[#0B1220]">{im.caption}</figcaption> : null}
          </figure>
        ))}
      </div>
      {filesEl}
      {lightboxEl}
    </>
  );
}

export function SectionsView({ sections = [], onRegister }) {
  return (
    <div className="space-y-12">
      {sections.map((sec, si) => (
        <motion.section key={sec.id || si} data-testid={`content-section-${si}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {sec.title ? <h2 className="font-display text-3xl tracking-tight mb-5">{sec.title}</h2> : null}
          <div className="space-y-6">
            {(sec.blocks || []).map((b, bi) => <BlockView key={b.id || bi} block={b} onRegister={onRegister} />)}
          </div>
        </motion.section>
      ))}
    </div>
  );
}

function BlockEditor({ block, si, bi, h }) {
  const controls = useDragControls();
  const [uploading, setUploading] = useState(false);
  const labels = { text: "Testo", image: "Immagine", table: "Tabella", video: "Video", gallery: "Galleria", file: "Allegato", registration: "Iscrizioni" };
  const icons = { text: Type, image: ImageIcon, table: TableIcon, video: Video, gallery: Images, file: Paperclip, registration: ClipboardList };
  const label = labels[block.type] || "Blocco";
  const Icon = icons[block.type] || Type;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAnyFile(file);
      h.updateBlock(block.id, { url: res.url, kind: res.kind, filename: res.name, size: res.size });
      toast.success("File caricato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const uploadGallery = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const current = block.images || [];
      const added = [];
      for (const file of Array.from(files)) {
        const res = await uploadAnyFile(file);
        added.push(res.kind === "image" ? { url: res.url, caption: "" } : { url: res.url, caption: "", kind: res.kind, filename: res.name, size: res.size });
      }
      h.updateBlock(block.id, { images: [...current, ...added] });
      toast.success(`${added.length} file caricati`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const setGalleryCaption = (idx, cap) => h.updateBlock(block.id, { images: (block.images || []).map((im, i) => (i === idx ? { ...im, caption: cap } : im)) });
  const removeGalleryImg = (idx) => h.updateBlock(block.id, { images: (block.images || []).filter((_, i) => i !== idx) });

  const uploadVideo = upload;
  const isAttachment = block.url && block.kind && block.kind !== block.type;

  const videoEmbed = block.type === "video" ? getVideoEmbed(block.url) : null;

  return (
    <Reorder.Item value={block} dragListener={false} dragControls={controls} data-testid={`block-${si}-${bi}`}
      className="rounded-lg border border-white/10 p-4 bg-background/40 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gold/80 inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</span>
        <div className="flex items-center gap-1">
          <button data-testid={`block-${si}-${bi}-drag`} onPointerDown={(e) => controls.start(e)} className="p-1.5 text-muted-foreground hover:text-gold cursor-grab active:cursor-grabbing touch-none" title="Trascina per spostare"><GripVertical className="w-4 h-4" /></button>
          <button data-testid={`block-${si}-${bi}-delete`} onClick={() => h.removeBlock(block.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {block.type === "text" && <RichTextEditor value={block.html} onChange={(v) => h.updateBlock(block.id, { html: v })} placeholder="Scrivi il testo…" />}
      {block.type === "image" && (() => {
        const items = imageItemsOf(block);
        const setItems = (next) => h.updateBlock(block.id, { images: next, url: "", caption: "", kind: undefined, filename: undefined, size: undefined, link: undefined });
        const onUpload = async (files) => {
          if (!files || !files.length) return;
          setUploading(true);
          try {
            const added = [];
            for (const file of Array.from(files)) {
              const res = await uploadAnyFile(file);
              added.push({ url: res.url, caption: "", kind: res.kind, filename: res.name, size: res.size });
            }
            setItems([...items, ...added]);
            toast.success(added.length > 1 ? `${added.length} immagini caricate` : "Immagine caricata");
          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          finally { setUploading(false); }
        };
        const updateItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
        const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
        return (
          <div className="space-y-3">
            {items.length > 0 && (
              <Reorder.Group axis="y" values={items} onReorder={setItems} className="space-y-3">
                {items.map((it, idx) => (
                  <BlockImageItem
                    key={it.url || idx}
                    item={it}
                    idx={idx}
                    si={si}
                    bi={bi}
                    onCaption={(v) => updateItem(idx, { caption: v })}
                    onLink={(v) => updateItem(idx, { link: v })}
                    onRemove={() => removeItem(idx)}
                  />
                ))}
              </Reorder.Group>
            )}
            <label data-testid={`block-${si}-${bi}-upload`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi immagine
              <input type="file" multiple className="hidden" onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} />
            </label>
            <p className="text-[11px] text-muted-foreground">Carica una o più immagini: trascina con la maniglia per riordinarle. Senza link il clic apre l'immagine a schermo intero; PDF/Office mostrano l'anteprima sfogliabile.</p>
          </div>
        );
      })()}
      {block.type === "table" && (
        <FlexibleTableEditor testid={`table-${si}-${bi}`} columns={block.columns || []} rows={block.rows || []} onChange={({ columns, rows }) => h.updateBlock(block.id, { columns, rows })} />
      )}
      {block.type === "video" && (
        <div className="space-y-2">
          <Input data-testid={`block-${si}-${bi}-video-url`} value={block.url} onChange={(e) => h.updateBlock(block.id, { url: e.target.value, kind: undefined, filename: undefined, size: undefined })} placeholder="Incolla link YouTube, Vimeo o URL .mp4" className="h-9" />
          <div className="flex items-center gap-2">
            <label data-testid={`block-${si}-${bi}-video-upload`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica video
              <input type="file" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
            </label>
            <span className="text-xs text-muted-foreground">oppure incolla un link qui sopra</span>
          </div>
          <Input data-testid={`block-${si}-${bi}-caption`} value={block.caption} onChange={(e) => h.updateBlock(block.id, { caption: e.target.value })} placeholder="Didascalia (facoltativa)" className="h-9" />
          {block.url && !videoEmbed && !isAttachment && <p className="text-xs text-destructive">Link video non riconosciuto (usa YouTube, Vimeo o un file .mp4).</p>}
          {(videoEmbed || isAttachment) && <div className="rounded-lg overflow-hidden border border-white/10"><BlockView block={block} /></div>}
        </div>
      )}
      {block.type === "gallery" && (
        <div className="space-y-3">
          {(block.images || []).length > 0 && (
            <Reorder.Group axis="y" values={block.images || []} onReorder={(next) => h.updateBlock(block.id, { images: next })} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(block.images || []).map((im, idx) => (
                <GalleryImageItem
                  key={im.url || idx}
                  item={im}
                  idx={idx}
                  si={si}
                  bi={bi}
                  onCaption={(v) => setGalleryCaption(idx, v)}
                  onRemove={() => removeGalleryImg(idx)}
                />
              ))}
            </Reorder.Group>
          )}
          <label data-testid={`block-${si}-${bi}-gallery-upload`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Aggiungi file
            <input type="file" multiple className="hidden" onChange={(e) => uploadGallery(e.target.files)} />
          </label>
          <p className="text-[11px] text-muted-foreground">Trascina le immagini con la maniglia in alto a sinistra per riordinarle.</p>
          <label data-testid={`block-${si}-${bi}-gallery-slideshow`} className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={!!block.slideshow} onChange={(e) => h.updateBlock(block.id, { slideshow: e.target.checked })} className="accent-[color:var(--gold)] w-4 h-4" />
            Modalità slideshow automatico (le immagini scorrono da sole)
          </label>
        </div>
      )}
      {block.type === "file" && (
        <div className="space-y-2">
          {block.url ? <BlockView block={block} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <label data-testid={`block-${si}-${bi}-file-upload`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {block.url ? "Sostituisci file" : "Carica file"}
              <input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            <Input data-testid={`block-${si}-${bi}-file-name`} value={block.filename || ""} onChange={(e) => h.updateBlock(block.id, { filename: e.target.value })} placeholder="Nome visualizzato (facoltativo)" className="h-9 flex-1 min-w-[180px]" />
          </div>
          <p className="text-xs text-muted-foreground">Qualsiasi formato: PDF, Word, Excel, PowerPoint, zip, audio, video… Viene mostrato come scheda con Apri/Scarica.</p>
        </div>
      )}
      {block.type === "registration" && (
        <div className="space-y-2">
          <Input data-testid={`block-${si}-${bi}-reg-title`} value={block.html} onChange={(e) => h.updateBlock(block.id, { html: e.target.value })} placeholder="Titolo (es. Iscrizione gare)" className="h-9" />
          <Input data-testid={`block-${si}-${bi}-reg-desc`} value={block.caption} onChange={(e) => h.updateBlock(block.id, { caption: e.target.value })} placeholder="Descrizione" className="h-9" />
          <p className="text-xs text-muted-foreground">Questo blocco mostra il pulsante di iscrizione con il modulo della disciplina.</p>
        </div>
      )}
    </Reorder.Item>
  );
}

function SectionEditor({ section, si, h }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={section} dragListener={false} dragControls={controls} data-testid={`section-card-${si}`}
      className="rounded-2xl border border-white/10 p-5 bg-[#0B1220]/40 space-y-4">
      <div className="flex items-center gap-2">
        <button data-testid={`section-${si}-drag`} onPointerDown={(e) => controls.start(e)} className="p-2 text-muted-foreground hover:text-gold cursor-grab active:cursor-grabbing touch-none" title="Trascina per spostare"><GripVertical className="w-4 h-4" /></button>
        <Input data-testid={`section-${si}-title`} value={section.title} onChange={(e) => h.updateSection(section.id, { title: e.target.value })} placeholder="Titolo sezione" className="font-display text-xl h-11 flex-1" />
        <button data-testid={`section-${si}-delete`} onClick={() => h.removeSection(section.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
      </div>

      <Reorder.Group axis="y" values={section.blocks} onReorder={(nb) => h.setBlocks(section.id, nb)} className="space-y-3">
        {section.blocks.map((b, bi) => (
          <BlockEditor key={b.id} block={b} si={si} bi={bi} h={{
            updateBlock: (bid, patch) => h.updateBlock(section.id, bid, patch),
            removeBlock: (bid) => h.removeBlock(section.id, bid),
          }} />
        ))}
      </Reorder.Group>

      <div className="flex flex-wrap gap-2">
        <Button data-testid={`section-${si}-add-text`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "text")} className="border-white/15 hover:border-gold/50"><Type className="w-3.5 h-3.5 mr-1.5" /> Testo</Button>
        <Button data-testid={`section-${si}-add-image`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "image")} className="border-white/15 hover:border-gold/50"><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Immagine</Button>
        <Button data-testid={`section-${si}-add-table`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "table")} className="border-white/15 hover:border-gold/50"><TableIcon className="w-3.5 h-3.5 mr-1.5" /> Tabella</Button>
        <Button data-testid={`section-${si}-add-video`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "video")} className="border-white/15 hover:border-gold/50"><Video className="w-3.5 h-3.5 mr-1.5" /> Video</Button>
        <Button data-testid={`section-${si}-add-gallery`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "gallery")} className="border-white/15 hover:border-gold/50"><Images className="w-3.5 h-3.5 mr-1.5" /> Galleria</Button>
        <Button data-testid={`section-${si}-add-file`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "file")} className="border-white/15 hover:border-gold/50"><Paperclip className="w-3.5 h-3.5 mr-1.5" /> Allegato</Button>
        <Button data-testid={`section-${si}-add-registration`} variant="outline" size="sm" onClick={() => h.addBlock(section.id, "registration")} className="border-white/15 hover:border-gold/50"><ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Iscrizioni</Button>
      </div>
    </Reorder.Item>
  );
}

export function SectionsEditor({ initial = [], onSave, onCancel, saving, onRegister }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(initial || [])));
  const [preview, setPreview] = useState(false);

  const updateSection = (sid, patch) => setDraft((d) => d.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const removeSection = (sid) => setDraft((d) => d.filter((s) => s.id !== sid));
  const addSection = () => setDraft((d) => [...d, { id: uid(), title: "Nuova sezione", blocks: [] }]);
  const setBlocks = (sid, blocks) => setDraft((d) => d.map((s) => (s.id === sid ? { ...s, blocks } : s)));
  const addBlock = (sid, type) => setDraft((d) => d.map((s) => {
    if (s.id !== sid) return s;
    const base = { id: uid(), type, html: "", url: "", caption: "" };
    if (type === "table") { base.columns = ["Colonna 1", "Colonna 2"]; base.rows = [["", ""]]; }
    if (type === "gallery") { base.images = []; }
    if (type === "registration") { base.html = "Iscrizione gare"; base.caption = "Compila il modulo di iscrizione dedicato alla disciplina."; }
    return { ...s, blocks: [...s.blocks, base] };
  }));
  const updateBlock = (sid, bid, patch) => setDraft((d) => d.map((s) => (s.id === sid ? { ...s, blocks: s.blocks.map((b) => (b.id === bid ? { ...b, ...patch } : b)) } : s)));
  const removeBlock = (sid, bid) => setDraft((d) => d.map((s) => (s.id === sid ? { ...s, blocks: s.blocks.filter((b) => b.id !== bid) } : s)));

  const h = { updateSection, removeSection, setBlocks, addBlock, updateBlock, removeBlock };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2 sticky top-16 z-10 py-2 bg-background/80 backdrop-blur rounded-lg">
        <Button data-testid="toggle-preview-btn" variant="outline" size="sm" onClick={() => setPreview((p) => !p)} className="border-white/15 hover:border-gold/60">
          {preview ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />} {preview ? "Nascondi anteprima" : "Anteprima"}
        </Button>
        <Button data-testid="save-page-btn" size="sm" onClick={() => onSave(draft)} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
        </Button>
        <Button data-testid="cancel-page-btn" size="sm" variant="ghost" onClick={onCancel}><X className="w-4 h-4 mr-1.5" /> Annulla</Button>
      </div>

      <div className={preview ? "grid lg:grid-cols-2 gap-8 items-start" : ""}>
        <div className="space-y-4">
          <Reorder.Group axis="y" values={draft} onReorder={setDraft} className="space-y-4">
            {draft.map((sec, si) => <SectionEditor key={sec.id} section={sec} si={si} h={h} />)}
          </Reorder.Group>
          <Button data-testid="add-section-btn" variant="outline" onClick={addSection} className="border-dashed border-white/20 hover:border-gold/50 w-full py-6"><Plus className="w-4 h-4 mr-1.5" /> Aggiungi sezione</Button>
        </div>
        {preview && (
          <div data-testid="live-preview" className="lg:sticky lg:top-32 rounded-2xl border border-gold/20 bg-[#0B1220]/40 p-6 max-h-[80vh] overflow-y-auto">
            <p className="text-xs uppercase tracking-wide text-gold/70 mb-4">Anteprima live</p>
            <SectionsView sections={draft} onRegister={onRegister} />
          </div>
        )}
      </div>
    </div>
  );
}
